import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { VideoModelSelect } from "./VideoModelSelect";

interface OpenRouterStatus {
  activeSource: "stored" | "env" | "none";
  hasStoredKey: boolean;
  hasEnvKey: boolean;
  baseUrl: string;
  defaultModel: string;
  storedKeyMasked: string | null;
  storedKeyUpdatedAt: string | null;
}

interface ConfigDialogProps {
  onClose: () => void;
}

export function ConfigDialog({ onClose }: ConfigDialogProps) {
  const toast = useToast();
  const [status, setStatus] = useState<OpenRouterStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const [defaultModel, setDefaultModel] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const s = await api.get<OpenRouterStatus>("/config/openrouter");
        if (!alive) return;
        setStatus(s);
        setBaseUrl(s.baseUrl);
        setDefaultModel(s.defaultModel);
      } catch (err: any) {
        toast.show(err?.message ?? "Failed to load config", "error");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, []);

  async function save() {
    setSaving(true);
    try {
      if (apiKey.trim()) {
        await api.put("/config/openrouter/key", { apiKey: apiKey.trim() });
        setApiKey("");
        setShowKey(false);
      }
      if (baseUrl.trim() || defaultModel.trim()) {
        await api.put("/config/openrouter/options", {
          baseUrl: baseUrl.trim() || undefined,
          defaultModel: defaultModel.trim() || undefined,
        });
      }
      const s = await api.get<OpenRouterStatus>("/config/openrouter");
      setStatus(s);
      setBaseUrl(s.baseUrl);
      setDefaultModel(s.defaultModel);
      toast.show("Settings saved", "success");
    } catch (err: any) {
      toast.show(err?.message ?? "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  async function removeKey() {
    if (!confirm("Remove the stored OpenRouter API key?")) return;
    setRemoving(true);
    try {
      await api.delete("/config/openrouter/key");
      const s = await api.get<OpenRouterStatus>("/config/openrouter");
      setStatus(s);
      toast.show("Stored key removed", "success");
    } catch (err: any) {
      toast.show(err?.message ?? "Failed", "error");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <h3>Settings</h3>

        {loading && <div className="muted tiny">Loading...</div>}

        {!loading && status && (
          <>
            <div className="field">
              <label>OpenRouter API key</label>
              <div className="row" style={{ alignItems: "center" }}>
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={status.hasStoredKey ? "Replace stored key" : "sk-or-..."}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button onClick={() => setShowKey((v) => !v)} type="button">
                  {showKey ? "Hide" : "Show"}
                </button>
              </div>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Stored safely in the project's database. The backend never returns
                the raw value through the API.
              </div>
              <div style={{ marginTop: 8 }}>
                {status.hasStoredKey ? (
                  <div className="tiny">
                    Stored key: <code>{status.storedKeyMasked}</code>
                    {status.storedKeyUpdatedAt && (
                      <span className="muted"> · saved {new Date(status.storedKeyUpdatedAt).toLocaleString()}</span>
                    )}
                    <button
                      className="danger"
                      style={{ marginLeft: 12, padding: "4px 10px", fontSize: 12 }}
                      disabled={removing}
                      onClick={removeKey}
                    >
                      {removing ? "Removing..." : "Remove stored key"}
                    </button>
                  </div>
                ) : (
                  <div className="tiny muted">No key stored yet.</div>
                )}
                {status.hasEnvKey && !status.hasStoredKey && (
                  <div className="tiny muted" style={{ marginTop: 4 }}>
                    Backend has <code>OPENROUTER_API_KEY</code> in its env. Adding
                    a key here will override it.
                  </div>
                )}
              </div>
              <div style={{ marginTop: 8 }}>
                <KeySource status={status} />
              </div>
            </div>

            <div className="field-row">
              <div>
                <label>OpenRouter base URL</label>
                <input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://openrouter.ai/api/v1"
                  spellCheck={false}
                />
              </div>
              <div>
                <label>Default video model</label>
                <VideoModelSelect
                  value={defaultModel}
                  onChange={setDefaultModel}
                />
              </div>
            </div>

            <div className="row between" style={{ marginTop: 16 }}>
              <button onClick={onClose}>Close</button>
              <button
                className="primary"
                disabled={saving}
                onClick={save}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function KeySource({ status }: { status: OpenRouterStatus }) {
  if (status.activeSource === "stored") {
    return (
      <span className="status-pill" style={{ color: "var(--success)", borderColor: "var(--success)" }}>
        Using stored key
      </span>
    );
  }
  if (status.activeSource === "env") {
    return (
      <span className="status-pill" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>
        Using env key
      </span>
    );
  }
  return (
    <span className="status-pill" style={{ color: "var(--danger)", borderColor: "var(--danger)" }}>
      No key configured
    </span>
  );
}
